import { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { withAuthorPanelSwitch, withStandardBaseCssClass } from '@adobe/aem-core-components-react-spa';
import { useMyTraining } from '../../../../lib/api/swrApi';
import { FacetType, FilterData } from '../../../../lib/utils/search/searchModel';
import { CourseCardProps } from '../../base/CourseCard/CourseCard.types';
import { pipe } from '../../../../lib/utils/functional';
import { filterTrainings, sortBy, withMyCoursesFacets } from './myCoursesFacets';
import {
  filterDataToParams,
  myCoursesFacetNames,
  onlySelectedFacets,
  SORT_BY_OPTIONS,
} from '../../../../lib/utils/search/searchHelper';
import { useSearchState } from '../../../../lib/utils/search/useSearchState';
import FilteredCardsView from '../../base/FilteredCardsView/FilteredCardsView';
import FiltersViewContext, { FiltersInitialViewContextType } from '../../../../lib/context/FiltersViewContext';
import { CommonFilterViewProps } from '../../base/FilteredCardsView/FilteredCardsView.types';
import { useConfig } from '../../../../lib/hooks/config';
import { pushAnalyticsEvent } from '../../../../lib/utils/analytics/analytics';
import { internalSearchTracking } from '../../../../lib/utils/analytics/analyticsHelper';
import useFeature from '../../../../lib/hooks/useFeature';
import { FeatureToggleCodename } from '../../../../lib/types/enums';
import { Api } from '../../../../lib/types/apiSpec';
import useErrorMapping from '../../../../lib/hooks/useErrorMapping';

export type MyCoursesProps = CommonFilterViewProps & { facets: Api.FacetName[] };

const MyCourses = ({ facets, ...rest }: MyCoursesProps) => {
  const globalConfig = useConfig();
  const { getParsedErrorMessage } = useErrorMapping();
  const { data, isLoading, error } = useMyTraining();
  const hideOverdue = useFeature(FeatureToggleCodename.hideOverdueDates);
  const { urlFilters, updateFilters, clearFilters } = useSearchState(
    [
      {
        title: '',
        filterGroups: myCoursesFacetNames,
        contentTypes: [],
        uniqueId: 'my-courses-id',
        isDefault: true,
      },
    ],
    !!hideOverdue,
  );
  const url = useLocation();

  const filterConfig = {
    complianceRetakePeriod: globalConfig?.complianceRetakePeriod ?? 0,
  };

  const filterFacets: FilterData<typeof FacetType.Checkbox> = useMemo(
    () =>
      pipe<FilterData<typeof FacetType.Checkbox>>(
        withMyCoursesFacets(data ?? [], filterConfig),
        onlySelectedFacets(facets),
      )(urlFilters),
    [urlFilters, data, facets],
  );

  const trainings = useMemo(
    () => pipe<CourseCardProps[]>(filterTrainings(filterFacets, filterConfig), sortBy(filterFacets))(data ?? []),
    [filterFacets, data],
  );

  const paginate = (arr: CourseCardProps[]) =>
    arr.slice(rest.pageSize * ((filterFacets.page ?? 1) - 1), rest.pageSize * (filterFacets.page ?? 1));

  const total = trainings.length;
  const trainingsToShow = paginate(trainings);

  const trackedFacets = useMemo(() => filterDataToParams({ ...filterFacets, page: undefined }), [filterFacets]);

  useEffect(() => {
    if (!isLoading) {
      const fullUrl = [url.pathname, trackedFacets].filter(Boolean).join('?');
      pushAnalyticsEvent(internalSearchTracking(total, fullUrl));
    }
  }, [trackedFacets, isLoading]);

  const filterViewContextValue = useMemo<FiltersInitialViewContextType>(
    () => ({
      ...rest,
      from: ((filterFacets.page ?? 1) - 1) * rest.pageSize + 1,
      to: Math.min(((filterFacets.page ?? 1) - 1) * rest.pageSize + rest.pageSize, total),
      total,
      filters: { ...filterFacets, totalCount: total },
      updateFilters,
      onClear: clearFilters,
      courses: trainingsToShow,
      withSearch: true,
      loading: isLoading ? 'initial' : null,
      fetchingError: error ? getParsedErrorMessage(error) : null,
      sortByOptions: SORT_BY_OPTIONS,
    }),
    [filterFacets, trainings, error],
  );

  return (
    <FiltersViewContext value={filterViewContextValue}>
      <FilteredCardsView />
    </FiltersViewContext>
  );
};

export default withStandardBaseCssClass(withAuthorPanelSwitch(MyCourses), 'cmp-tabs');
